import React, { createRef, Suspense, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  BypassBlock,
  classNames,
  Helmet,
  ToastMode,
  ToastProvider,
  useDefineMessages,
  useToasts,
  TooltipProvider,
} from '@groma-dev/blueprint';
import { initializeAuthInterceptor, SiteExp } from '@groma-dev/scaffold';
import { Route, Routes, useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import Cookies from 'js-cookie';
import { AppConfig } from '../../../core/AppConfig';
import { jumpToAnchorRef, scrollToTop } from '../../utils/WebHelper';
import { useAppStore, useDataStore } from '../../providers/StoresProvider';
import { ComponentFactory } from '../../ComponentFactory';
import { AppRoutes } from '../../../core/routes';
import { APIClient } from '../../../core/apiclient/APIClient';
import AuthRouter from '../authrouter/AuthRouter';
import OnboardingRouter from '../onboardingrouter/OnboardingRouter';
import { AppMode } from '../../stores/AppStoreInterface';
import { handleErrorLogging } from '../../../core/utils/CoreHelper';
import { LocalStorage } from '../../../core/utils/Constants';
import { getAppStyles } from './styles/App.styles';

const AccreditationFlow = React.lazy(() => ComponentFactory.AccreditationFlowAsync);
const AccreditedInvestor = React.lazy(() => ComponentFactory.AccreditedInvestorAsync);
const AccountSelect = React.lazy(() => ComponentFactory.AccountSelectAsync);
const AddEntityContactInfo = React.lazy(() => ComponentFactory.AddEntityContactInfoAsync);
const AddEntityDetails = React.lazy(() => ComponentFactory.AddEntityDetailsAsync);
const ClaimVerifyIdentity = React.lazy(() => ComponentFactory.ClaimVerifyIdentityAsync);
const CreateInvestmentAccount = React.lazy(() => ComponentFactory.CreateInvestmentAccountAsync);
const Documents = React.lazy(() => ComponentFactory.DocumentsAsync);
const ErrorPage = React.lazy(() => ComponentFactory.ErrorPageAsync);
const GetStarted = React.lazy(() => ComponentFactory.GetStartedAsync);
const Help = React.lazy(() => ComponentFactory.HelpAsync);
const Home = React.lazy(() => ComponentFactory.HomeAsync);
const Invest = React.lazy(() => ComponentFactory.InvestAsync);
const InvestAmount = React.lazy(() => ComponentFactory.InvestAmountAsync);
const InvestConfirmation = React.lazy(() => ComponentFactory.InvestConfirmationAsync);
const InvestDocuments = React.lazy(() => ComponentFactory.InvestDocumentsAsync);
const InvestPayment = React.lazy(() => ComponentFactory.InvestPaymentAsync);
const InvestReview = React.lazy(() => ComponentFactory.InvestReviewAsync);
const InvestVerifyId = React.lazy(() => ComponentFactory.InvestVerifyIdAsync);
const Overview = React.lazy(() => ComponentFactory.OverviewAsync);
const SelectEntityType = React.lazy(() => ComponentFactory.SelectEntityTypeAsync);
const Settings = React.lazy(() => ComponentFactory.SettingsAsync);
const SideNav = React.lazy(() => ComponentFactory.SideNavAsync);
const VerifyEmail = React.lazy(() => ComponentFactory.VerifyEmailAsync);
const VerifyIdentity = React.lazy(() => ComponentFactory.VerifyIdentityAsync);
const VerifyInformation = React.lazy(() => ComponentFactory.VerifyInformationAsync);
const Wallet = React.lazy(() => ComponentFactory.WalletAsync);
const WelcomeComplete = React.lazy(() => ComponentFactory.WelcomeCompleteAsync);

const GromaCoinWelcome = React.lazy(() => ComponentFactory.GromaCoinWelcomeAsync);
const GromaCoinTransaction = React.lazy(() => ComponentFactory.GromaCoinTransactionAsync);
const GromaCoinNextSteps = React.lazy(() => ComponentFactory.GromaCoinNextStepsAsync);
const GromaCoinVerify = React.lazy(() => ComponentFactory.GromaCoinVerifyAsync);
const GromaCoinWaitlist = React.lazy(() => ComponentFactory.GromaCoinWaitlistAsync);
const UniswapPage = React.lazy(() => ComponentFactory.UniswapPageAsync);

let authRedirectInitialized = false;

// These pages handle their own 401s: their visitors have no portal session to send to sign-in.
// With the flag off, /uniswap falls through to the portal, which needs the sign-in redirect.
const getAuthInterceptorIgnoredPaths = () => {
  if (!AppConfig.Pages.Uniswap.enabled) return [AppRoutes.GromaCoinRoot];

  return [AppRoutes.GromaCoinRoot, AppRoutes.Uniswap];
};

const buildSignInUrl = () => {
  const { Domains } = AppConfig.Settings;
  const { pathname, search } = window.location;
  const returnUrl = encodeURIComponent(`${pathname}${search}`);

  return `${Domains.id.baseUrl}/signin?return_url=${returnUrl}&site_exp=${SiteExp.INVESTORS}`;
};

export interface AppProps {}

const GromaCoinRoutes = observer(() => {
  const { Seo } = AppConfig.Settings;

  return (
    <>
      {Seo.enabled && (
        <Helmet>
          <title>GromaCoin Claim</title>
        </Helmet>
      )}
      <Suspense fallback={<ComponentFactory.Loading isPage />}>
        <Routes>
          <Route index element={<GromaCoinWelcome />} />
          <Route path="claim/:claimId" element={<GromaCoinWelcome />} />
          <Route path="transaction" element={<GromaCoinTransaction />} />
          <Route path="next-steps" element={<GromaCoinNextSteps />} />
          <Route path="verify" element={<GromaCoinVerify />} />
          <Route path="waitlist" element={<GromaCoinWaitlist />} />
        </Routes>
      </Suspense>
    </>
  );
});

const AppContent = observer(() => {
  const dataStore = useDataStore();
  const appStore = useAppStore();
  const messages = useDefineMessages();
  const { mode } = appStore.state;
  const { formatMessage } = useIntl();
  const styles = getAppStyles();
  const { addToast } = useToasts();
  const { userState, accountsState } = dataStore;
  const { pathname } = useLocation();

  const fetchAccount = async (id: string) => {
    try {
      const response = await dataStore.fetchAccount({ id });

      if (response.success) {
        dataStore.setAccount(response.account);
      } else {
        setTimeout(
          () => addToast(response.error?.message ?? formatMessage(messages.commonError), ToastMode.ERROR),
          100
        );
      }
    } catch (e) {
      handleErrorLogging(e as Error);
    }
  };

  const fetchAccounts = async () => {
    const { pageSize } = AppConfig.Components.App.accounts;
    try {
      const firstPage = await dataStore.fetchAccounts({
        page_size: pageSize,
        page: 1,
      });

      if (firstPage.status === 404) {
        dataStore.setAccounts([]);
        return;
      }

      if (!firstPage.success) {
        setTimeout(
          () => addToast(firstPage.error?.message ?? formatMessage(messages.commonError), ToastMode.ERROR),
          100
        );
        return;
      }

      if (firstPage.count == null) {
        throw new Error('Paginated accounts response missing count field');
      }
      const totalPages = Math.ceil(firstPage.count / pageSize);

      const remainingPages = await Promise.all(
        Array.from({ length: Math.max(0, totalPages - 1) }, (_, index) =>
          dataStore.fetchAccounts({
            page_size: pageSize,
            page: index + 2,
          })
        )
      );

      const failedPage = remainingPages.find(response => !response.success);
      if (failedPage) {
        setTimeout(
          () => addToast(failedPage.error?.message ?? formatMessage(messages.commonError), ToastMode.ERROR),
          100
        );
        return;
      }

      const allAccounts = [...firstPage.accounts, ...remainingPages.flatMap(response => response.accounts)];
      dataStore.setAccounts(allAccounts);

      if (allAccounts.length > 0) {
        const lsInvestmentAccount = dataStore.getLocalStorage(LocalStorage.InvestmentAccount);
        const validAccountStored =
          lsInvestmentAccount && allAccounts.some(acc => acc.id === parseInt(lsInvestmentAccount));
        await fetchAccount(validAccountStored ? lsInvestmentAccount : `${allAccounts[0].id}`);
      }
    } catch (e) {
      handleErrorLogging(e as Error);
    } finally {
      dataStore.setFetchingAccounts(false);
      dataStore.setAppInitialized(true);
    }
  };

  useEffect(() => {
    if (userState.user) {
      if (!accountsState.isFetching && !accountsState.accounts) {
        fetchAccounts();
      }
    }
  }, [userState.user]);

  if (!dataStore.appState.initialized) return <ComponentFactory.Loading isPage />;

  return (
    <>
      <ComponentFactory.Header />
      <div id="mainContent" className={styles.mainContent}>
        <ComponentFactory.ErrorBoundary>
          <Suspense fallback={<ComponentFactory.Loading isPage />}>
            {mode === AppMode.Desktop && pathname !== AppRoutes.root && <SideNav />}
            {mode !== AppMode.Desktop && pathname !== AppRoutes.root && (
              <div className={classNames(styles.mobileAccountSelectContainer)}>
                <AccountSelect />
              </div>
            )}
            <Routes>
              <Route path={AppRoutes.root} element={<Home />} />
              <Route path={AppRoutes.Accreditation} element={<AccreditationFlow />} />
              <Route path={AppRoutes.Overview} element={<Overview />} />
              <Route path={AppRoutes.Wallet} element={<Wallet />} />
              <Route path={AppRoutes.Documents} element={<Documents />} />
              <Route path={AppRoutes.Settings} element={<Settings />} />
              <Route path={AppRoutes.Invest} element={<Invest />} />
              <Route path={AppRoutes.InvestAmount} element={<InvestAmount />} />
              <Route path={AppRoutes.InvestVerifyId} element={<InvestVerifyId />} />
              <Route path={AppRoutes.InvestDocuments} element={<InvestDocuments />} />
              <Route path={AppRoutes.InvestPayment} element={<InvestPayment />} />
              <Route path={AppRoutes.InvestReview} element={<InvestReview />} />
              <Route path={AppRoutes.InvestConfirmation} element={<InvestConfirmation />} />
              <Route path={AppRoutes.Help} element={<Help />} />
              <Route path="*" element={<ErrorPage />} />
            </Routes>
          </Suspense>
        </ComponentFactory.ErrorBoundary>
      </div>
      <ComponentFactory.Footer />
    </>
  );
});

const App = observer(() => {
  const styles = getAppStyles();
  const { pathname, hash } = useLocation();
  const { Authentication } = AppConfig.Settings;
  const { name: authCookieName, domain } = Authentication.cookie;
  const resetRef = createRef<HTMLSpanElement>();

  if (Authentication.enabled && Cookies.get(authCookieName) && !authRedirectInitialized) {
    initializeAuthInterceptor(APIClient, buildSignInUrl, { name: authCookieName, domain }, 401, {
      ignoredPathPrefixes: getAuthInterceptorIgnoredPaths(),
    });
    authRedirectInitialized = true;
  }

  useEffect(() => {
    if (hash) jumpToAnchorRef();
    else {
      scrollToTop();
      if (!hash) resetRef.current?.focus();
    }
  }, [pathname]);

  return (
    <>
      <span ref={resetRef} tabIndex={-1} />
      <BypassBlock id="mainContent">
        <FormattedMessage
          id="common.bypassBlocks.mainContent"
          defaultMessage="Skip to Main Content"
          description="Main bypass block content"
        />
      </BypassBlock>
      <ToastProvider>
        <div className={classNames(styles.mainContainer)}>
          <Routes>
            <Route
              path="/gromacoin/*"
              element={
                <ComponentFactory.ErrorBoundary>
                  <GromaCoinRoutes />
                </ComponentFactory.ErrorBoundary>
              }
            />
            {AppConfig.Pages.Uniswap.enabled && (
              <Route
                path={`${AppRoutes.Uniswap}/*`}
                element={
                  <ComponentFactory.ErrorBoundary>
                    <Suspense fallback={<ComponentFactory.Loading isPage />}>
                      <UniswapPage />
                    </Suspense>
                  </ComponentFactory.ErrorBoundary>
                }
              />
            )}
            <Route
              path="*"
              element={
                <ComponentFactory.ErrorBoundary>
                  <TooltipProvider>
                    <Routes>
                      <Route path={AppRoutes.Auth} element={<ComponentFactory.Auth />} />
                      <Route
                        path={AppRoutes.OnboardingGetStarted}
                        element={
                          <OnboardingRouter>
                            <GetStarted />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.Onboarding}
                        element={
                          <OnboardingRouter>
                            <GetStarted />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.OnboardingVerifyEmail}
                        element={
                          <OnboardingRouter>
                            <VerifyEmail />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.OnboardingCreateAccount}
                        element={
                          <OnboardingRouter>
                            <CreateInvestmentAccount />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.OnboardingSelectEntityType}
                        element={
                          <OnboardingRouter>
                            <SelectEntityType />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.OnboardingVerifyIdentity}
                        element={
                          <OnboardingRouter>
                            <VerifyIdentity />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.OnboardingAddEntityDetails}
                        element={
                          <OnboardingRouter>
                            <AddEntityDetails />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.OnboardingAddEntityContactInfo}
                        element={
                          <OnboardingRouter>
                            <AddEntityContactInfo />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.OnboardingVerifyInformation}
                        element={
                          <OnboardingRouter>
                            <VerifyInformation />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.OnboardingAccreditedInvestor}
                        element={
                          <OnboardingRouter>
                            <AccreditedInvestor />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.OnboardingWelcomeComplete}
                        element={
                          <OnboardingRouter>
                            <WelcomeComplete />
                          </OnboardingRouter>
                        }
                      />
                      <Route
                        path={AppRoutes.OnboardingClaimVerifyIdentity}
                        element={
                          <Suspense fallback={<ComponentFactory.Loading isPage />}>
                            <ClaimVerifyIdentity />
                          </Suspense>
                        }
                      />
                      <Route
                        path="*"
                        element={
                          <AuthRouter>
                            <AppContent />
                          </AuthRouter>
                        }
                      />
                    </Routes>
                  </TooltipProvider>
                </ComponentFactory.ErrorBoundary>
              }
            />
          </Routes>
        </div>
      </ToastProvider>
    </>
  );
});

export default App;
